import type { ProblemDetail } from '../types';

import type { CsrfScope } from './csrf';
import { getCsrfToken } from './csrf';
import { ApiError } from './error';

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

function emitUnauthorized(): void {
  for (const fn of unauthorizedListeners) {
    try {
      fn();
    } catch {
      // listeners must not throw — swallow defensively
    }
  }
}

/**
 * Refreshes the in-memory CSRF token for a scope (typically by re-fetching
 * `/auth/me` or `/admin/session`, which mint a fresh token). Registered by
 * the host's auth context so the client can self-heal a stale token.
 */
type CsrfRefresher = () => Promise<void>;
const csrfRefreshers: Partial<Record<CsrfScope, CsrfRefresher>> = {};

/**
 * Register the refresher used to recover a rejected CSRF token for `scope`.
 *
 * A double-submit CSRF token can go stale without the session ending — the
 * server rotates it on `/auth/me` when the client isn't holding a match
 * (concurrent `/me` polls, a StrictMode double-mount, an HMR reset of the
 * in-memory store). A rejected token comes back as **403**, so the
 * {@link onUnauthorized} channel (401-only) never sees it and nothing
 * refreshes the token — every subsequent write keeps failing until a full
 * reload. When a mutating request 403s, the client calls this refresher once
 * and retries, so the stale token heals itself transparently.
 */
export function registerCsrfRefresher(scope: CsrfScope, fn: CsrfRefresher): () => void {
  csrfRefreshers[scope] = fn;
  return () => {
    if (csrfRefreshers[scope] === fn) {
      delete csrfRefreshers[scope];
    }
  };
}

export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface ApiClient {
  get<T>(path: string, opts?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
}

function csrfScopeForPath(path: string): import('./csrf').CsrfScope | null {
  if (path.includes('/admin/')) return 'admin';
  // Commuter-v1: any mutating call against the user-cookie surface
  // needs the user CSRF token. `/auth/signout` is the most user-facing
  // example; `/me/*` covers the saved-stop writes.
  if (path.includes('/auth/signout') || path.includes('/me/')) return 'user';
  return null;
}

async function readProblem(response: Response): Promise<ProblemDetail | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return null;
  }
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    return null;
  }
}

async function request<T>(
  baseUrl: string,
  method: string,
  path: string,
  body: unknown,
  opts: RequestOptions | undefined,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts?.headers ?? {}),
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const isMutating = method !== 'GET' && method !== 'HEAD';
  const scope = isMutating ? csrfScopeForPath(path) : null;
  if (scope) {
    const token = getCsrfToken(scope);
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  } else if (path === '/api/v1/auth/me') {
    // Send the user CSRF on /me so the server can echo it back instead
    // of rotating. Without this, a second tab's /me poll would silently
    // invalidate the first tab's in-memory token (review MEDIUM #8).
    const token = getCsrfToken('user');
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  } else if (path === '/api/v1/admin/session') {
    // Same echo-vs-rotate contract as /me, for the admin scope: send the
    // current admin CSRF on the session re-check so the server echoes it
    // back rather than rotating and invalidating another tab's token.
    const token = getCsrfToken('admin');
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  const url = `${baseUrl}${path}`;
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (opts?.signal) {
    init.signal = opts.signal;
  }

  let response = await fetch(url, init);

  // Self-heal a stale CSRF token. A rejected double-submit token comes back
  // as 403 (not the 401 the onUnauthorized channel watches), so without this
  // a stale token would fail every write until a full reload. Refresh the
  // scope's token once and retry the same request.
  if (isMutating && scope && response.status === 403) {
    const refresher = csrfRefreshers[scope];
    if (refresher) {
      try {
        await refresher();
      } catch {
        // Refresh failed (network/5xx) — fall through and surface the 403.
      }
      const refreshed = getCsrfToken(scope);
      if (refreshed && refreshed !== headers['X-CSRF-Token']) {
        headers['X-CSRF-Token'] = refreshed;
        response = await fetch(url, init);
      }
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const problem = await readProblem(response);
    if (response.status === 401) {
      emitUnauthorized();
    }
    const title = problem?.title ?? (response.statusText || 'Request failed');
    throw new ApiError(response.status, title, problem?.detail ?? null, problem);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function createApiClient(baseUrl: string = ''): ApiClient {
  return {
    get: <T>(path: string, opts?: RequestOptions) =>
      request<T>(baseUrl, 'GET', path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(baseUrl, 'POST', path, body, opts),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(baseUrl, 'PUT', path, body, opts),
    del: <T>(path: string, opts?: RequestOptions) =>
      request<T>(baseUrl, 'DELETE', path, undefined, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(baseUrl, 'PATCH', path, body, opts),
  };
}

/**
 * The shared singleton the typed `endpoints` call through. Its base URL is
 * injected by the host app at startup via {@link configureApiClient}, since
 * the base URL comes from a platform-specific source (`import.meta.env` on
 * the Vite web app, `process.env.EXPO_PUBLIC_*` in the Expo mobile app) that
 * this framework-agnostic package must not reach for directly.
 *
 * It delegates to a mutable underlying client so reconfiguring the base URL
 * never invalidates references already captured by `endpoints.ts`.
 */
let activeClient: ApiClient = createApiClient('');
let activeBaseUrl = '';

export function configureApiClient(baseUrl: string): void {
  activeBaseUrl = baseUrl;
  activeClient = createApiClient(baseUrl);
}

/**
 * The currently configured backend base URL. Endpoints that hit server-root
 * paths outside `/api/v1` (health checks, the Prometheus `/metrics` scrape)
 * read it here since they bypass the {@link apiClient}.
 */
export function getApiBaseUrl(): string {
  return activeBaseUrl;
}

export const apiClient: ApiClient = {
  get<T>(path: string, opts?: RequestOptions) {
    return activeClient.get<T>(path, opts);
  },
  post<T>(path: string, body?: unknown, opts?: RequestOptions) {
    return activeClient.post<T>(path, body, opts);
  },
  put<T>(path: string, body?: unknown, opts?: RequestOptions) {
    return activeClient.put<T>(path, body, opts);
  },
  del<T>(path: string, opts?: RequestOptions) {
    return activeClient.del<T>(path, opts);
  },
  patch<T>(path: string, body?: unknown, opts?: RequestOptions) {
    return activeClient.patch<T>(path, body, opts);
  },
};
